import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ElementDetailsV3 } from "./ElementDetailsV3";
import { InspectorHeader } from "../InspectorHeader";
import { GridReviewButton } from "../GridReviewButton";
import { TraversalControls } from "../TraversalControls";
import { JumpToGroupButton } from "../JumpToGroupButton";
import { ConfirmActionModal } from "../ConfirmActionModal";
import { ONBOARDING_ANCHORS } from "../Onboarding/onboardingSelectors";
import { sumElementMultipliers } from "../utils/sumElementMultipliers";
import { useKeyAction } from "hooks/useKeyAction";
import { isFormControlFocused } from "utils/isFormControlFocused";
import type { ElementInspectorV3Props } from "./types";

export const ElementInspectorV3 = ({
  canEdit,
  form,
  selectedGroupId,
  groupElements,
  selectedElement,
  groupLvMatch,
  boqPositions,
  onSaveLvMatch,
  groupTypeOptions,
  onSelectElement,
  onRenameGroup,
  nameStatus,
  onMarkReviewed,
  onToggleSuspicious,
  isEditingElement,
  onToggleEditMode,
  onDeleteElement,
  onDeleteGroup,
  isTraversing,
  onStartTraversing,
  onStopTraversing,
  onOpenGridReview,
  isGridReviewOpen = false,
  seenIds,
  isAllElementsMode = false,
  onJumpToGroup,
  hiddenElementIds,
  onToggleElementsVisibility,
}: ElementInspectorV3Props) => {
  const { t } = useTranslation();

  const [pendingDeleteElementId, setPendingDeleteElementId] = useState<
    string | null
  >(null);

  const [isDeleteGroupConfirmOpen, setIsDeleteGroupConfirmOpen] =
    useState(false);

  // Traversal walks one marker at a time; the header reports how many elements those markers are worth.
  const markerCount = groupElements.length;
  const elementQuantity = useMemo(
    () => sumElementMultipliers({ elements: groupElements }),
    [groupElements],
  );

  const currentElementIndex = useMemo(
    () =>
      selectedElement
        ? groupElements.findIndex((el) => el.id === selectedElement.id)
        : -1,
    [groupElements, selectedElement],
  );

  const handlePreviousElement = useCallback(() => {
    if (currentElementIndex > 0) {
      onSelectElement(groupElements[currentElementIndex - 1]);
    }
  }, [currentElementIndex, groupElements, onSelectElement]);

  const handleNextElement = useCallback(() => {
    if (currentElementIndex < groupElements.length - 1) {
      onSelectElement(groupElements[currentElementIndex + 1]);
    }
  }, [currentElementIndex, groupElements, onSelectElement]);

  const handleGoToElementIndex = useCallback(
    (index: number) => {
      if (index >= 0 && index < groupElements.length) {
        onSelectElement(groupElements[index]);
      }
    },
    [groupElements, onSelectElement],
  );

  const handleNextUnseen = useCallback(() => {
    for (let offset = 1; offset < groupElements.length; offset++) {
      const index = (currentElementIndex + offset) % groupElements.length;
      if (!seenIds.has(groupElements[index].id)) {
        onSelectElement(groupElements[index]);
        return;
      }
    }
  }, [currentElementIndex, groupElements, seenIds, onSelectElement]);

  const seenCount = useMemo(
    () => groupElements.filter((element) => seenIds.has(element.id)).length,
    [groupElements, seenIds],
  );

  const handleDeleteShortcut = useCallback(
    (event: KeyboardEvent) => {
      if (!selectedElement) return;
      if (isFormControlFocused(event)) return;
      setPendingDeleteElementId(selectedElement.id);
    },
    [selectedElement],
  );

  useKeyAction({
    code: "KeyD",
    callback: handleDeleteShortcut,
    options: {
      isDisabled:
        !canEdit ||
        !isTraversing ||
        isGridReviewOpen ||
        !selectedElement ||
        isEditingElement ||
        pendingDeleteElementId !== null,
    },
  });

  if (!selectedGroupId) {
    return (
      <div className="bg-white p-4 h-full overflow-hidden flex flex-col">
        <p className="text-sm text-gray-700">
          {t("smartSurveyor.groupInspector.emptyState")}
        </p>
        <div className="mt-3 flex-1 bg-blue-50 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="bg-white h-full overflow-hidden flex flex-col">
      <InspectorHeader
        canEdit={canEdit}
        groupName={selectedGroupId}
        groupTypeOptions={groupTypeOptions}
        groupColor={groupElements[0]?.rgb}
        onRenameGroup={onRenameGroup}
        nameStatus={nameStatus}
        onMarkReviewed={onMarkReviewed}
        onToggleSuspicious={onToggleSuspicious}
        countLabel={t("smartSurveyor.groupInspector.elementsLabel")}
        count={elementQuantity}
        lvMatch={groupLvMatch}
        boqPositions={boqPositions}
        onSaveLvMatch={onSaveLvMatch}
        isTraversing={isTraversing}
        onStartTraversing={onStartTraversing}
        onStopTraversing={onStopTraversing}
        isGroupNameEditable={!isAllElementsMode}
        isLvMatchVisible={!isAllElementsMode}
        onDeleteGroup={
          isAllElementsMode || !onDeleteGroup
            ? undefined
            : () => setIsDeleteGroupConfirmOpen(true)
        }
        dataOnboarding={ONBOARDING_ANCHORS.inspectorHeader}
        groupNameDataOnboarding={ONBOARDING_ANCHORS.inspectorGroupName}
        startReviewDataOnboarding={
          ONBOARDING_ANCHORS.inspectorStartReviewButton
        }
      />
      {isTraversing && (
        <div className="overflow-y-auto flex flex-col min-h-0 w-full flex-1 px-4 py-3 gap-3">
          {markerCount > 0 && (
            <TraversalControls
              editingLabel={t("smartSurveyor.groupInspector.editingElement")}
              headerAction={
                onOpenGridReview && (
                  <GridReviewButton onOpenGridReview={onOpenGridReview} />
                )
              }
              selectedIndex={currentElementIndex}
              total={markerCount}
              seenCount={seenCount}
              onPrev={handlePreviousElement}
              onNext={handleNextElement}
              onSelectIndex={handleGoToElementIndex}
              onNextUnseen={handleNextUnseen}
              isAllMode={isAllElementsMode}
            />
          )}
          {isAllElementsMode && selectedElement && onJumpToGroup && (
            <JumpToGroupButton
              groupName={selectedElement.group_id}
              onClick={onJumpToGroup}
            />
          )}
          {selectedElement ? (
            <ElementDetailsV3
              canEdit={canEdit}
              form={form}
              element={selectedElement}
              groupTypeOptions={groupTypeOptions}
              isEditingElement={isEditingElement}
              onToggleEditMode={onToggleEditMode}
              onDelete={() => setPendingDeleteElementId(selectedElement.id)}
              isElementHidden={hiddenElementIds.has(selectedElement.id)}
              onToggleElementVisibility={() =>
                onToggleElementsVisibility([selectedElement.id])
              }
              dataOnboarding={ONBOARDING_ANCHORS.elementDetails}
            />
          ) : (
            <div className="flex items-center justify-center flex-1 text-sm text-gray-400 p-4">
              {t("smartSurveyor.groupInspector.noElementSelected")}
            </div>
          )}
        </div>
      )}
      <ConfirmActionModal
        isOpen={!!pendingDeleteElementId}
        onClose={() => setPendingDeleteElementId(null)}
        onConfirm={() => {
          if (pendingDeleteElementId) {
            onDeleteElement(pendingDeleteElementId);
            setPendingDeleteElementId(null);
          }
        }}
        action="delete"
        title={t("smartSurveyor.elementDetails.deleteModal.title")}
        description={t("smartSurveyor.elementDetails.deleteModal.description")}
      />
      <ConfirmActionModal
        isOpen={isDeleteGroupConfirmOpen}
        onClose={() => setIsDeleteGroupConfirmOpen(false)}
        onConfirm={() => onDeleteGroup?.()}
        action="delete"
        title={t("smartSurveyor.groupInspector.deleteGroupModal.title")}
        description={t(
          "smartSurveyor.groupInspector.deleteGroupModal.elementDescription",
        )}
      />
    </div>
  );
};
